import type { IDropdownButtonProps } from "@similarweb/ui-components";
import type { IEllipsisDropdownItemProps } from "@similarweb/ui-components";
import { translate } from "app/filters/i18nFilter";
import { objectKeys } from "app/helpers/objects";
import useDelayedAction from "app/pages/sales-intelligence/hooks/useDelayedAction";
import useUncontrolledDropdown from "app/pages/sales-intelligence/hooks/useUncontrolledDropdown";
import FilterSelectAllItem from "app/pages/sales-intelligence/sub-modules/signals/components/filters/shared/FilterDropdown/FilterSelectAllItem/FilterSelectAllItem";
import { StyledMultiSelectCategoryDropdownItem } from "app/pages/sales-intelligence/sub-modules/signals/components/filters/SignalNewsFilter/styles";
import type { CategoryDropdownOption } from "app/pages/sales-intelligence/sub-modules/signals/components/filters/SignalNewsFilter/types/CategoryDropdownOption";
import {
    DROPDOWN_HEIGHT,
    DROPDOWN_WIDTH,
} from "app/pages/sales-intelligence/sub-modules/signals/components/filters/SignalNewsFilter/utils/const";
import {
    isCategoryOfType,
    isParentCategory,
} from "app/pages/sales-intelligence/sub-modules/signals/components/filters/SignalNewsFilter/utils/dropdownOptions";
import { DROPDOWN_AUTO_OPEN_DELAY } from "app/pages/sales-intelligence/sub-modules/signals/constants/dropdown";
import useSignalFilterDropdownLabel from "app/pages/sales-intelligence/sub-modules/signals/hooks/useSignalFilterDropdownLabel";
import SignalFilterDropdownLabel from "app/pages/sales-intelligence/sub-modules/signals/components/filters/shared/SignalFilterDropdownLabel/SignalFilterDropdownLabel";
import type { NewsCategory } from "app/pages/sales-intelligence/sub-modules/signals/models/news/NewsCategory";
import type { NewsCategoryType } from "app/pages/sales-intelligence/sub-modules/signals/models/news/NewsCategoryType";
import { FilterType } from "app/pages/sales-intelligence/sub-modules/signals/models/services/FilterService";
import filterService from "app/pages/sales-intelligence/sub-modules/signals/services/filters/filterService";
import { matchNewsCategoryToName } from "app/pages/sales-intelligence/sub-modules/signals/utils/news/filters";
import { useCallback } from "react";

import useMultiselectHandler from "../../../hooks/useMultiselectHandler";
import type { NewsCategoriesByType } from "../../../models/news/NewsCategoriesByType";
import FilterButtonLabel from "../shared/FilterDropdown/FilterButtonLabel/FilterButtonLabel";
import SignalFilterDropdownButton from "app/pages/sales-intelligence/sub-modules/signals/components/filters/shared/FilterDropdown/SignalFilterDropdownButton/SignalFilterDropdownButton";
import FilterMultiselectDropdown from "../shared/FilterMultiselectDropdown/FilterMultiselectDropdown";
import useOptionsWithParentCategories from "./hooks/useOptionsWithParentCategories";

type OptionId = CategoryDropdownOption["id"];
type DropdownButtonProps = Pick<IDropdownButtonProps, "isOpen">;

type FilterValue = readonly NewsCategory[] | null | undefined;

export type SignalAnyNewsFilterProps = {
    filterKey: string;
    defaultOpen?: boolean;
    isActive?: boolean;
    isValueEmpty: boolean;
    selectedValue: FilterValue;
    options: Readonly<NewsCategoriesByType>;
    onValueChange(filterValue: FilterValue): void;
    onToggle?(isOpen: boolean): void;
};

const SignalAnyNewsFilter = (props: SignalAnyNewsFilterProps) => {
    const { filterKey, options, selectedValue, isValueEmpty, defaultOpen = false, onValueChange, onToggle } = props;
    const categoryTypes = objectKeys(options);
    const allOptions = Object.values(options).flat();

    const { dropdownRef, onUncontrolledOpen } = useUncontrolledDropdown();

    useDelayedAction(DROPDOWN_AUTO_OPEN_DELAY, onUncontrolledOpen, defaultOpen);

    const { selectedValueWithCategories, optionsWithCategories } = useOptionsWithParentCategories(
        selectedValue ?? [],
        options,
    );

    const { onSelect } = useMultiselectHandler({
        selectedValue,
        onValueChange,
    });

    const buildFilterName = useCallback(() => {
        const translationOptions = {
            i18nKeyModifier: filterKey,
        };
        const nameBuilder = filterService.getFilterName(FilterType.AnyNewsCategories);

        return nameBuilder.buildName({ categories: selectedValue }, options, translationOptions);
    }, [filterKey, options, selectedValue]);

    const resolvePrimaryLabel = useCallback(() => {
        const firstCategory = selectedValue?.[0];

        return firstCategory ? translate(matchNewsCategoryToName(firstCategory).key) : "";
    }, [selectedValue]);

    const { model: filterLabelModel } = useSignalFilterDropdownLabel({
        buildName: buildFilterName,
        resolvePrimaryLabel,
    });

    const isFullSelection = selectedValue?.length === allOptions.length;

    const matchOptionToCategory = (categoryType: NewsCategoryType) => {
        const matchesCategoryType = isCategoryOfType(categoryType);

        return (entry: CategoryDropdownOption): boolean => matchesCategoryType(entry) && !isParentCategory(entry);
    };

    const handleOptionSelect = (category: NewsCategory, isSelected: boolean) => {
        const selectedOption = allOptions.find((optionId) => optionId === category);

        if (typeof selectedOption === "undefined") {
            console.error("SignalAnyNewsFilter: unable to select news option");
            return;
        }

        onSelect(selectedOption, isSelected);
    };

    const handleCategorySelect = (categoryType: NewsCategoryType, isOptionSelected: boolean) => {
        const categoryValue = optionsWithCategories
            .filter(matchOptionToCategory(categoryType))
            .map(({ id }) => id) as readonly NewsCategory[];

        onSelect(categoryValue, isOptionSelected);
    };

    const handleChange = (value: OptionId | null, isSelected: boolean) => {
        if (value === null) {
            const changedValues = isSelected ? [] : allOptions;

            onValueChange(changedValues);
            return;
        }

        if (categoryTypes.includes(value as NewsCategoryType)) {
            handleCategorySelect(value as NewsCategoryType, isSelected);
            return;
        }

        handleOptionSelect(value as NewsCategory, isSelected);
    };

    const renderItem = (item: CategoryDropdownOption) => {
        const { key } = matchNewsCategoryToName(item.id);

        return (
            <StyledMultiSelectCategoryDropdownItem
                {...item}
                key={item.id}
                icon={!item.isChild ? "category" : undefined}
                text={translate(key)}
            />
        );
    };

    const renderButton = ({ isOpen }: DropdownButtonProps) => {
        return (
            <SignalFilterDropdownButton
                id={filterKey}
                isOpen={isOpen}
                hasValue={!isValueEmpty}
            >
                <FilterButtonLabel>
                    <SignalFilterDropdownLabel model={filterLabelModel} />
                </FilterButtonLabel>
            </SignalFilterDropdownButton>
        );
    };

    const renderSelectAllItem = (props: IEllipsisDropdownItemProps) => {
        return (
            <FilterSelectAllItem {...props}>
                {translate(`si.signals.filters.${filterKey}.name.any`)}
            </FilterSelectAllItem>
        );
    };

    return (
        <FilterMultiselectDropdown
            ref={dropdownRef}
            width={DROPDOWN_WIDTH}
            dropdownPopupHeight={DROPDOWN_HEIGHT}
            filterKey={filterKey}
            isFullSelection={isFullSelection}
            selectedOptions={selectedValueWithCategories}
            buttonComponent={renderButton}
            selectAllComponent={renderSelectAllItem}
            hasSearch
            onChange={handleChange}
            onToggle={onToggle}
        >
            {optionsWithCategories.map(renderItem)}
        </FilterMultiselectDropdown>
    );
};

export default SignalAnyNewsFilter;
